/**
 * The game's animals, each kind its own: the wild hare in the fields (since 0.80), the museum attic's spiders (1.04),
 * and the skies' creatures of 0.98.4: the Mothman of dusk, the Yeti of the snow and the thunderstorm's street rats.
 * Speeds are in game units per millisecond (the player's base speed is 0.03).
 */
export interface AnimalDefinition {
    readonly idString: string
    /** sent with each animal (`AnimalKind`): which it is */
    readonly kind: AnimalKind
    readonly health: number
    /** collision radius; the player's is 2.25 */
    readonly radius: number
    readonly wanderSpeed: number
    readonly fleeSpeed: number
    /** a player this close makes it bolt; within calmRadius it freezes and watches */
    readonly fleeRadius: number
    readonly calmRadius: number
    /** a shot fired this close makes it bolt */
    readonly noiseRadius: number
    /** how long a move lasts, and a pause between moves, while it wanders (ms) */
    readonly wanderMove: readonly [number, number]
    readonly wanderPause: readonly [number, number]
    /** how much it turns between moves while it wanders (degrees, either way) */
    readonly wanderTurn: number
    /** what it drops when it's killed */
    readonly loot: string
    /** how many of it (one unless said) */
    readonly lootCount?: number
    /** and anything else it drops with it (the Yeti's wood) */
    readonly extraLoot?: ReadonlyArray<{ readonly idString: string, readonly count: number }>
    /** a carcass stays this long after death */
    readonly carcassMs: number
    /** the floors it may stand on besides grass and sand (the rats: the roads and the stone round the houses) */
    readonly roads?: boolean
    /** a flier (the Mothman): it lives on the roofs and comes down now and then (`Mothman` says how) */
    readonly flier?: boolean
}

export const enum AnimalKind {
    Hare,
    Spider,
    Mothman,
    Yeti,
    Rat
}

/**
 * The wild hare: roams the fields, freezes when someone gets close, bolts away in zig-zag hops when
 * a player comes near or a shot is fired close by, and drops Field Meat when it is killed.
 */
export const Hare: AnimalDefinition = Object.freeze<AnimalDefinition>({
    idString: "hare",
    kind: AnimalKind.Hare,
    health: 15,
    radius: 1.35,
    wanderSpeed: 0.0105,
    fleeSpeed: 0.039,
    fleeRadius: 35,
    calmRadius: 60,
    noiseRadius: 70,
    wanderMove: [600, 1300],
    wanderPause: [800, 2500],
    wanderTurn: 40,
    loot: "meat",
    carcassMs: 30_000
});

/**
 * The museum attic's spiders (1.04; the owner, 25 October 2026: "add harmless scary looking spiders (2) that give meat
 * when shot"): hairy, red-eyed, a pale skull on the back, about a player across their legs (1.04.1, the owner the same
 * day: "spider looks too big": half 1.04's size, the hitbox with them). They creep about the attic's boards in short
 * scuttles and stops, freeze when someone comes near and scuttle off when they come close or a shot goes off; they
 * never hurt anyone. Shot (or hit) dead, each drops Field Meat. They keep to the attic (Game.spawnAtticSpiders), under
 * its dark (the page draws them there: seen where a light falls).
 */
export const Spider: AnimalDefinition = Object.freeze<AnimalDefinition>({
    idString: "attic_spider",
    kind: AnimalKind.Spider,
    health: 30,
    // (its body, 1.7 by 1 in its picture, and the legs round it a little; 1.9 in 1.04, at twice the size)
    radius: 1,
    wanderSpeed: 0.022,
    fleeSpeed: 0.05,
    // (under the dark since 1.04.1, a spider is seen in the glow round you, which fades out a few steps off: it lets you
    // come that close, frozen and watching from 22 away, before it bolts; 16 and 34 in 1.04, out of the glow's reach)
    fleeRadius: 8,
    calmRadius: 22,
    noiseRadius: 60,
    wanderMove: [250, 650],
    wanderPause: [700, 2600],
    wanderTurn: 70,
    loot: "meat",
    carcassMs: 45_000
});

/**
 * The Mothman of dusk (0.98.4, owner decision, 31 October 2026: "add a mothman, its dark, has red eyes, and only flies
 * to roofs. only two in the map, in two different sides. Every once in awhile, he comes down to the ground, and thats
 * when he's shooteable, gives 2 meat, he's also shooteable in roofs, but like i said, every once in awhile, like once
 * every minute, goes to the ground for 5 seconds, before going back to the roof. Also, he has 40 health, he also tries
 * to escape gunshots, 2 seconds after being shot first time he flies back out"): a dark winged figure with red eyes
 * that lives on the roofs' eaves (`MothmanWays`: the server's `Animal` perches it and flies it), two of them on a dusk
 * match, one each side of the map. Perched or down on the ground it can be shot; in the air it can't (a bullet flies
 * under it). Shot dead, it drops two Field Meat.
 */
export const Mothman: AnimalDefinition = Object.freeze<AnimalDefinition>({
    idString: "mothman",
    kind: AnimalKind.Mothman,
    health: 40,
    // (its body, a head and shoulders over a player; its wings reach well past: fourteen units across in flight.
    // 0.98.5: 3.4, drawn bigger at the owner's word, "Mothman looks small"; 2.6 in 0.98.4)
    radius: 3.4,
    // (on the ground it walks a few steps between its glances; it never flees on foot: it flies)
    wanderSpeed: 0.012,
    fleeSpeed: 0.012,
    fleeRadius: 0,
    calmRadius: 30,
    noiseRadius: 0,
    wanderMove: [400, 900],
    wanderPause: [500, 1400],
    wanderTurn: 60,
    loot: "meat",
    lootCount: 2,
    carcassMs: 60_000,
    flier: true
});

/** the Mothman's ways (ms and units): its timings are the owner's, 31 October 2026 */
export const MothmanWays = Object.freeze({
    /** two on the map, one each side (west and east halves) */
    count: 2,
    /** how often it comes down to the ground (from its last landing back on a roof) */
    groundEveryMs: 60_000,
    /** how long it stays down */
    groundMs: 5_000,
    /** shot for the first time (on the ground or on a roof), it flies off this long after */
    escapeMs: 2_000,
    /** how far from its perch it comes down, at most (open ground, never in a building) */
    groundReach: 46,
    /** how far it flies to another roof when it moves, at most, and the least */
    roofHop: Object.freeze({ min: 30, max: 240 }),
    /** its flight: how fast across the ground, and the least time a flight takes (a short hop still lifts and lands) */
    flySpeed: 0.035,
    minFlightMs: 1_400,
    /** perched, it moves to another roof every so often (ms, between) */
    restMs: Object.freeze([70_000, 140_000] as const),
    /** a perch's spot on a roof: the eave, this far out from the wall's line (its hitbox reaches past the wall: shot from the ground) */
    eave: 1.3
});

/**
 * The Yeti of the snow (0.98.4, owner decision, 31 October 2026: "On snow mode, add one yeti that gives 2 meat and 5
 * wood, thje animation and his design need to be very good. also, his footsteps look really big and different from
 * human footsteps"): one on a snow match, a huge white-furred thing lumbering over the fields, leaving great prints in
 * the snow (the page draws them, deep and wide, where it walks: war/warWeather.ts). It stands and watches whoever comes
 * near, roars, and lumbers off when they come close or a shot goes off; it never hurts anyone. Killed, it drops two
 * Field Meat and five Wood.
 */
export const Yeti: AnimalDefinition = Object.freeze<AnimalDefinition>({
    idString: "yeti",
    kind: AnimalKind.Yeti,
    health: 150,
    // (a head and shoulders over a player: a third bigger than its first drawing)
    radius: 3.4,
    wanderSpeed: 0.011,
    // (slower than a player running at it: it can be caught)
    fleeSpeed: 0.026,
    fleeRadius: 20,
    calmRadius: 48,
    noiseRadius: 80,
    wanderMove: [900, 2200],
    wanderPause: [1200, 4000],
    wanderTurn: 35,
    loot: "meat",
    lootCount: 2,
    extraLoot: [{ idString: "wood", count: 5 }],
    carcassMs: 60_000
});

/** the Yeti's prints (units): a stride between two, each print's length (a boot's is about 1.6) */
export const YetiPrints = Object.freeze({ stride: 7.4, length: 5.2, width: 3.8 });

/**
 * The thunderstorm's street rats (0.98.4, owner decision, 31 October 2026: "add small street rats that give 1 meat"):
 * small, quick, along the roads and the gutters, scurrying off when anyone comes near. Killed, one Field Meat.
 */
export const Rat: AnimalDefinition = Object.freeze<AnimalDefinition>({
    idString: "street_rat",
    kind: AnimalKind.Rat,
    health: 8,
    radius: 0.6,
    wanderSpeed: 0.02,
    fleeSpeed: 0.052,
    fleeRadius: 11,
    calmRadius: 20,
    noiseRadius: 40,
    wanderMove: [250, 700],
    wanderPause: [400, 1800],
    wanderTurn: 80,
    loot: "meat",
    carcassMs: 30_000,
    roads: true
});

/** how many rats a thunderstorm has on a town map (one per so many square units, between the two) */
export const RatCount = Object.freeze({ perArea: 300_000, min: 6, max: 12 });

/** every kind, by its `AnimalKind` */
export const AnimalDefinitions: readonly AnimalDefinition[] = [Hare, Spider, Mothman, Yeti, Rat];

export const animalOfKind = (kind: number): AnimalDefinition => AnimalDefinitions[kind] ?? Hare;

export const enum AnimalState {
    Wander,
    Alert,
    Flee,
    Dead,
    /** a flier perched on a roof (0.98.4) */
    Perch,
    /** a flier in the air: between a roof and the ground, or two roofs (0.98.4); `height` says how high */
    Fly
}

/** a flier's height in the air, sent as a byte: 0 on the ground or a roof, 255 at the top of its flight */
export const heightByte = (height: number): number => Math.round(Math.min(1, Math.max(0, height)) * 255);
export const heightFromByte = (byte: number): number => byte / 255;
